<template>
    <!-- Loading: skeleton rows instead of a bare spinner -->
    <ul v-if="list === undefined" class="queue-list" aria-busy="true" aria-label="Loading queue">
        <li v-for="n in 4" :key="n" class="queue-row queue-row--skeleton">
            <div class="queue-row__head">
                <Skeleton shape="circle" size="2.75rem" />
                <div class="queue-row__info">
                    <Skeleton width="60%" height="1.1rem" />
                    <Skeleton width="35%" height="0.85rem" class="mt-2" />
                </div>
            </div>
            <div class="queue-row__actions">
                <Skeleton height="2.75rem" borderRadius="0.5rem" />
                <Skeleton height="2.75rem" borderRadius="0.5rem" />
            </div>
        </li>
    </ul>

    <!-- Empty: teach the next action -->
    <div v-else-if="!list.length" class="queue-empty">
        <i class="pi pi-inbox queue-empty__icon" aria-hidden="true"></i>
        <p class="queue-empty__title">Queue is clear</p>
        <p class="queue-empty__hint">Scan a badge or add an email below to start a new entry.</p>
    </div>

    <!-- List -->
    <transition-group v-else tag="ul" name="queue" class="queue-list">
        <li v-for="item in list" :key="item.id ?? `${item.screen_number}-${item.date_time}`" class="queue-row">
            <div class="queue-row__head">
                <span class="queue-row__screen" aria-hidden="true">{{ screenLabel(item) }}</span>
                <div class="queue-row__info">
                    <p class="queue-row__name">{{ item.name || 'Captured link' }}</p>
                    <p class="queue-row__meta">
                        <span class="queue-row__screen-text">Screen {{ item.screen_number }}</span>
                        <span class="queue-row__dot" aria-hidden="true">·</span>
                        <span>{{ formatQueueTime(item.date_time) }}</span>
                    </p>
                </div>
                <Button icon="pi pi-trash" text rounded severity="secondary"
                    class="queue-row__delete" aria-label="Delete entry" @click="onDeleteItemClick(item)" />
            </div>
            <div class="queue-row__actions">
                <Button class="queue-action-btn queue-email-btn" :loading="props.loading"
                    @click="onQueueItemClick(item)">
                    <i class="pi pi-envelope"></i>
                    <span>Email</span>
                </Button>
                <Scanner fluid outlined :autoSubmit="autoSubmitScanner" template="badge"
                    @scaned="(data) => onScannerScaned(item, data)" />
            </div>
        </li>
    </transition-group>
</template>

<script setup>
import { ref } from 'vue';
import Skeleton from 'primevue/skeleton';
import Scanner from '@/components/Scanner.vue';
import AutomateService from "@/services/automate";
import { useToast } from 'primevue/usetoast';
import { formatQueueTime } from '@/utils/helpers';

const toast = useToast();
const emit = defineEmits(['send-email', 'delete']);
// Set true if you want to auto submit scanner
const autoSubmitScanner = ref(process.env.VUE_APP_AUTO_SUBMIT_SCANNER == '1');

const props = defineProps({
    list: {
        type: Array,
        default: () => undefined,
    },
    loading: {
        type: Boolean,
        default: () => false,
    },
    autoProcess: {
        type: Boolean,
        default: () => false
    }
});

const screenLabel = (item) => {
    const n = item.screen_number;
    return n === undefined || n === null || n < 0 ? '—' : String(n);
};

const onScannerScaned = async (queueData, scanedData) => {
    try {
        const { data: response } = await AutomateService.getBadgeInfo(scanedData);
        const formData = AutomateService.badgeInfoToEmailForm(response);
        formData.queueData = queueData;
        emit('send-email', formData, props.autoProcess, 'scanner');
    } catch (error) {
        toast.add({ severity: 'error', summary: 'Submission failed. Please try again.', life: 3000 });
    }
};

const onQueueItemClick = (queueData) => {
    emit('send-email', { queueData }, false, 'button');
};

const onDeleteItemClick = (queueData) => {
    emit('delete', queueData);
};
</script>

<style lang="scss" scoped>
.queue-list {
    position: relative;
    list-style: none;
    margin: 0;
    padding: 0;
}

.queue-row {
    padding: 0.875rem 0;

    & + .queue-row {
        border-top: 1px solid var(--surface-200);
    }
}

.queue-row__head {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.queue-row__screen {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 0.75rem;
    background-color: #fff3d6;
    color: #8a5a00;
    font-weight: 800;
    font-size: 1.15rem;
    line-height: 1;
}

.queue-row__info {
    flex: 1 1 auto;
    min-width: 0;
}

.queue-row__name {
    margin: 0;
    font-size: 1.0625rem;
    font-weight: 600;
    color: var(--text-color);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.queue-row__meta {
    margin: 0.15rem 0 0;
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.875rem;
    /* Darker than the default muted gray to clear 4.5:1 on white. */
    color: var(--surface-600);
}

.queue-row__dot {
    opacity: 0.6;
}

.queue-row__delete {
    flex: 0 0 auto;
    width: 2.75rem;
    height: 2.75rem;
}

.queue-row__delete :deep(.pi) {
    font-size: 1.15rem;
}

.queue-row__actions {
    display: flex;
    gap: 0.625rem;
    margin-top: 0.75rem;

    > * {
        flex: 1 1 0;
        min-width: 0;
    }
}

/* Email = brand yellow (ties to the envelope/"Add email" action); Scan stays
   the theme purple. Forced across states so the theme hover can't override. */
.p-button.queue-email-btn,
.p-button.queue-email-btn:enabled:hover,
.p-button.queue-email-btn:enabled:active,
.p-button.queue-email-btn:focus {
    background-color: #ffc34e;
    border-color: #ffc34e;
    color: #3a2a00;
}

/* Empty state */
.queue-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 0.4rem;
    padding: 3.5rem 1.5rem;
    color: var(--surface-600);
}

.queue-empty__icon {
    font-size: 2.75rem;
    color: var(--surface-400);
    margin-bottom: 0.5rem;
}

.queue-empty__title {
    margin: 0;
    font-size: 1.15rem;
    font-weight: 700;
    color: var(--text-color);
}

.queue-empty__hint {
    margin: 0;
    max-width: 26ch;
    font-size: 0.95rem;
    line-height: 1.4;
}

/* Row enter/leave + reorder motion */
.queue-enter-from {
    opacity: 0;
    transform: translateY(-6px);
}

.queue-enter-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
}

.queue-leave-active {
    transition: opacity 0.18s ease, transform 0.18s ease;
    position: absolute;
    width: 100%;
}

.queue-leave-to {
    opacity: 0;
    transform: translateX(8px);
}

.queue-move {
    transition: transform 0.2s ease;
}

@media (prefers-reduced-motion: reduce) {

    .queue-enter-active,
    .queue-leave-active,
    .queue-move {
        transition: none;
    }
}
</style>
